import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  useMediaQuery,
  Chip,
  Pagination,
  Menu,
  MenuItem,
  InputBase,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTheme } from "../../../../contexts/ThemeContext";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList"; // ✅ filter icon
import ExportDropdown from "../../../../components/ExportDropdown";
import CustomText, { textStyles } from "../../../../components/CustomText";
import AttendanceDetailsDrawer from "./DetailsDrawer.js";
import AttendanceMap from "./MapTab.js";
import AttendanceCalendar from "./CalenderTab.js";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useTranslation } from "react-i18next";
import TruncatedCell from "../../../../components/TruncatedCell.js";
import AttendanceFilterDrawer from "./FilterDrawer.js"; // ✅ your drawer
import empty from "../../../../assets/empty.png";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import defaulttable from "../../../../assets/Attendance/defaulttable.svg";
import activetable from "../../../../assets/Attendance/activetable.svg";
import mapicon from "../../../../assets/Attendance/mapicon.svg";
import activemap from "../../../../assets/Attendance/activemap.svg";
import calender from "../../../../assets/Attendance/calender.svg";
import activecalender from "../../../../assets/Attendance/activecalender.svg";
import ExportAttendanceHistoryExcel from "./HistoryExcel.js";
import {
  getStatusBadgeBackgroundColor,
  getStatusBadgeForegroundColor,
} from "../../../../constants/statusPalette";
import { getAttendanceHistoryColorKey } from "./attendanceHistoryStatusOptions";
import ExportAttendanceHistoryPDF from "./HistoryPdf.js";
import { pdf } from "@react-pdf/renderer";
import { useCompanyProfile } from "../../../../components/CompanyProfile";
import {
  convertUTCToLocalDateTime,
  formatCalendarDateForDisplay,
} from "../../../../components/dateTimeUtils";

const AttendanceHistory = () => {
  const { t } = useTranslation();
  const { primaryColor, secondaryColor, primaryTextColor, secondaryTextColor } = useTheme();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
const normalizeStatusKey = (status) =>
  status
    ?.toLowerCase()
    .replace(/\s+/g, "")       // "Early Out" → "earlyout"
    .replace(/-/g, "");   

const toTitleCase = (text = "") =>
  text.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    
const formatStatus = (status) => {
  const keyMap = {
    present: "attendanceStatus.present",
    earlyout: "attendanceStatus.early_clock_out",
    latearrival: "attendanceStatus.late_clock_in",
    lateout: "attendanceStatus.late_clock_out",
    lateandearlyout: "attendanceStatus.lateAndEarlyOut",
    leave: "attendanceStatus.leave",
    absent: "attendanceStatus.absent",
    halfleave: "attendanceStatus.halfLeave",
    hasissues: "attendanceStatus.unvalidatedPunches",
    halfday: "attendanceStatus.halfDay",
    overtime: "attendanceStatus.overtime"
  };

  const normalized = normalizeStatusKey(status);
  const translatedLabel = keyMap[normalized] ? t(keyMap[normalized]) : status;
  return translatedLabel ? toTitleCase(translatedLabel) : "--";
};

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  const [historyData, setHistoryData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
const [initialLoading, setInitialLoading] = useState(true); // only first time
const [tableLoading, setTableLoading] = useState(false); 
  // ✅ filter drawer state
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ status: "", startDate: "", endDate: "",   workerId: "",   });
const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [searchBulkRows, setSearchBulkRows] = useState([]);

  const isMobile = useMediaQuery("(max-width:768px)");
  const searchActive = Boolean(searchTerm.trim());
  const LIST_PAGE_SIZE = 5;

  const formatRecordDate = useCallback((rec, firstClockIn, lastClockOut) => {
    const dateSource =
      firstClockIn?.occurredAt ||
      lastClockOut?.occurredAt ||
      rec.date;
    return formatCalendarDateForDisplay(dateSource);
  }, []);

  const mapRecordToRow = useCallback((rec) => {
    const clockIns = rec.punches?.filter((p) => p.actionType === "CLOCK_IN") || [];
    const clockOuts = rec.punches?.filter((p) => p.actionType === "CLOCK_OUT") || [];
    const firstClockIn = clockIns[0];
    const lastClockOut = clockOuts[clockOuts.length - 1];
    const breakStarts = rec.punches?.filter((p) => p.actionType === "BREAK_START") || [];
    const breakEnds = rec.punches?.filter((p) => p.actionType === "BREAK_END") || [];
    const breakPairs = breakStarts.map((start, index) => {
      const end = breakEnds[index];
      return {
        start: start?.occurredAt || "--",
        end: end?.occurredAt || "--",
      };
    });
    return {
      id: rec.worker.employeeId,
      fullName: rec.worker.fullName,
      email: rec.worker?.email || "—",
      department: rec.worker?.department || "—",
      date: formatRecordDate(rec, firstClockIn, lastClockOut),
      checkIn: firstClockIn?.occurredAt || "—",
      checkIns: clockIns.map((p) => p?.occurredAt).filter(Boolean),
      breaks: breakPairs,
      checkOut: lastClockOut?.occurredAt || "—",
      checkOuts: clockOuts.map((p) => p?.occurredAt).filter(Boolean),
      location: rec.locationSummary || "-",
      details: `${t("attendanceHistory.issues")}: ${rec.attendance.issuesCount}`,
      status: rec.statusBadge?.text || "-",
      raw: rec,
    };
  }, [t, formatRecordDate]);

const formatUTCToLocal = (utcString) => {
  return convertUTCToLocalDateTime(utcString, {
    locale: "en-GB",
    formatOptions: {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    },
  });
};




useEffect(() => {
  if (searchActive) return;

  const isFirstLoad = initialLoading;
  if (!isFirstLoad) setTableLoading(true);

  let url = `${BASE_URL}/company-admins/attendance/history?pageSize=${LIST_PAGE_SIZE}&page=${currentPage}`;

  // Apply filters to URL
  if (filters.status) url += `&status=${filters.status}`;
  if (filters.startDate) url += `&dateFrom=${filters.startDate}`;
  if (filters.endDate) url += `&dateTo=${filters.endDate}`;
  if (filters.workerId) url += `&workerId=${filters.workerId}`;

  console.log("📡 Fetching attendance history with URL:", url);

  fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${decryptedToken}`,
    },
  })
    .then((res) => res.json())
    .then((data) => {
      console.log("✅ API response:", data);

      if (!data.error && data.data?.records) {
        const mapped = data.data.records.map(mapRecordToRow);

        console.log("Mapped Data for Table:", mapped);

        setHistoryData(mapped);
        setTotalPages(data.data.pagination.totalPages || 1);
      } else {
        setHistoryData([]);
        setTotalPages(1);
      }
    })
    .catch((err) => {
      console.error("❌ Error fetching attendance history:", err);
      setHistoryData([]);
    })
    .finally(() => {
      if (isFirstLoad) {
        setInitialLoading(false);
      } else {
        setTableLoading(false);
      }
    });
}, [currentPage, decryptedToken, filters, searchActive, mapRecordToRow]);

  useEffect(() => {
    if (!decryptedToken || !searchActive) {
      setSearchBulkRows([]);
      return;
    }

    let cancelled = false;

    const fetchAllPagesForSearch = async () => {
      setTableLoading(true);
      const apiPageSize = 100;
      try {
        let allRaw = [];
        let page = 1;
        let pagesTotal = 1;
        do {
          let url = `${BASE_URL}/company-admins/attendance/history?pageSize=${apiPageSize}&page=${page}`;
          if (filters.status) url += `&status=${filters.status}`;
          if (filters.startDate) url += `&dateFrom=${filters.startDate}`;
          if (filters.endDate) url += `&dateTo=${filters.endDate}`;
          if (filters.workerId) url += `&workerId=${filters.workerId}`;

          const res = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${decryptedToken}`,
            },
          });
          const data = await res.json();
          if (cancelled) return;
          if (data.error || !data.data?.records?.length) break;
          allRaw = allRaw.concat(data.data.records);
          pagesTotal = Math.max(1, data.data.pagination?.totalPages ?? 1);
          page += 1;
        } while (page <= pagesTotal);

        setSearchBulkRows(allRaw.map(mapRecordToRow));
        setCurrentPage(1);
      } catch (err) {
        console.error("❌ Error fetching attendance history for search:", err);
        if (!cancelled) setSearchBulkRows([]);
      } finally {
        if (!cancelled) {
          setTableLoading(false);
        }
      }
    };

    fetchAllPagesForSearch();
    return () => {
      cancelled = true;
    };
  }, [decryptedToken, filters, searchActive, mapRecordToRow]);


  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const HeaderStyles = {
    color: "#44546F",
    fontSize: "15px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
      whiteSpace: 'nowrap', // ✅ Prevent text wrap
   
      overflow: 'hidden',
  textOverflow: 'ellipsis',
  };

  const DataStyles = {
    color: "#172B4D",
    fontSize: "13px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
      whiteSpace: 'nowrap', // ✅ Prevent text wrap
   
      overflow: 'hidden',
  textOverflow: 'ellipsis',
  };

  const sourceRows = searchActive ? searchBulkRows : historyData;
  const termLower = searchTerm.trim().toLowerCase();
  const filteredAll = !termLower
    ? sourceRows
    : sourceRows.filter(
        (row) =>
          row.fullName.toLowerCase().includes(termLower) ||
          (row.email && String(row.email).toLowerCase().includes(termLower)) ||
          row.location.toLowerCase().includes(termLower) ||
          row.details.toLowerCase().includes(termLower) ||
          row.status.toLowerCase().includes(termLower)
      );
  const paginationCount = searchActive
    ? Math.max(1, Math.ceil(filteredAll.length / LIST_PAGE_SIZE))
    : totalPages;
  const effectiveSearchPage = searchActive
    ? Math.min(currentPage, paginationCount)
    : currentPage;
  const rowsForTable = searchActive
    ? filteredAll.slice(
        (effectiveSearchPage - 1) * LIST_PAGE_SIZE,
        effectiveSearchPage * LIST_PAGE_SIZE
      )
    : filteredAll;

const handleApplyFilters = (newFilters) => {
  // Log the filters applied
  console.log("Applied Filters:", newFilters);  // Logs the filters being applied
  
  setFilters(newFilters); // Update the filters state
  setCurrentPage(1); // Reset pagination on filter change
  setIsFilterApplied(!!newFilters.status || !!newFilters.startDate || !!newFilters.endDate ); // Check if any filter is applied
};

const fetchAllHistory = async (decryptedToken, filters = {}) => {
  try {
    const queryParams = new URLSearchParams({
      no_pagination: "true"
    });

    // Apply filters
    if (filters.status) queryParams.append("status", filters.status);
    if (filters.startDate) queryParams.append("dateFrom", filters.startDate);
    if (filters.endDate) queryParams.append("dateTo", filters.endDate);
    if (filters.workerId) queryParams.append("workerId", filters.workerId);


    const url = `${BASE_URL}/company-admins/attendance/history?${queryParams.toString()}`;
    console.log("📡 Attendance Export API URL:", url);

    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${decryptedToken}`,
        "Content-Type": "application/json",
      },
    });

    const json = await res.json();
    console.log("📄 EXPORT JSON RESPONSE:", json);

    if (json.error) {
      console.error("❌ Export API error:", json.message);
      return [];
    }

    const records = json.data?.records || [];

    return records.map((rec) => {
      const clockIns = rec.punches?.filter((p) => p.actionType === "CLOCK_IN") || [];
      const clockOuts = rec.punches?.filter((p) => p.actionType === "CLOCK_OUT") || [];
      const firstClockIn = clockIns[0];
      const lastClockOut = clockOuts[clockOuts.length - 1];
const breakStarts = rec.punches?.filter(p => p.actionType === "BREAK_START") || [];
const breakEnds = rec.punches?.filter(p => p.actionType === "BREAK_END") || [];

// Pair them in order
const breakPairs = breakStarts.map((start, index) => {
  const end = breakEnds[index];
  return {
    start: start?.occurredAt || "--",
    end: end?.occurredAt || "--"
  };
});
      const checkInTimes = clockIns.map((p) => p?.occurredAt).filter(Boolean);
      const checkOutTimes = clockOuts.map((p) => p?.occurredAt).filter(Boolean);
      const formatDisplayTimes = (times, single) =>
        times.length > 0
          ? getCompactTimes(times)
              .map((time) => (time === "..." ? "..." : formatTimeTo12Hour(time)))
              .join(", ")
          : formatTimeTo12Hour(single);
      const formatBreakDisplay = (pairs, key) =>
        pairs.length > 0
          ? getCompactTimes(pairs.map((b) => b?.[key]))
              .map((time) => (time === "..." ? "..." : formatTimeTo12Hour(time)))
              .join(", ")
          : "--";

      return {
        id: rec.worker?.employeeId || "-",
        fullName: rec.worker?.fullName || "-",
        email: rec.worker?.email || "—",
        date: formatRecordDate(rec, firstClockIn, lastClockOut),
        checkIn: formatDisplayTimes(checkInTimes, firstClockIn?.occurredAt),
        checkOut: formatDisplayTimes(checkOutTimes, lastClockOut?.occurredAt),
        checkIns: checkInTimes,
        checkOuts: checkOutTimes,
           breaks: breakPairs,
        breakStart: formatBreakDisplay(breakPairs, "start"),
        breakEnd: formatBreakDisplay(breakPairs, "end"),
        location: rec.locationSummary || "-",
        details: `Issues: ${rec.attendance?.issuesCount ?? "-"}`,
        status: formatStatus(rec.statusBadge?.text || "-"),
        raw: rec
      };
    });

  } catch (err) {
    console.error("❌ Export ERROR:", err);
    return [];
  }
};



const { logoBase64 } = useCompanyProfile(decryptedToken);
const formatTimeTo12Hour = (timeString) => {
  const localValue = convertUTCToLocalDateTime(timeString, {
    locale: "en-GB",
    formatOptions: { hour: "numeric", minute: "2-digit", hour12: true },
  });
  return localValue === "—" ? "--" : localValue;
};

const getCompactTimes = (times = []) => {
  const safeTimes = (times || []).filter(Boolean);
  if (safeTimes.length <= 2) return safeTimes;
  return [safeTimes[0], "...", safeTimes[safeTimes.length - 1]];
};

const renderCompactTimeCell = (time, key) => {
  if (time === "...") {
    return <div key={key}>...</div>;
  }

  return <div key={key}>{formatTimeTo12Hour(time)}</div>;
};

const [selectedMapRow, setSelectedMapRow] = useState(null);

useEffect(() => {
  setSelectedMapRow(null); // 🔥 IMPORTANT
}, [filters.status, filters.startDate, filters.endDate, filters.workerId]);
  return (
    <>
    <Box sx={{ml:1,mr:1,mt:1,}}>
    <AttendanceMap
  page={currentPage}
  pageSize={5}
  status={filters.status}
  startDate={filters.startDate}
  workerId={filters.workerId}   // ✅ ADD

  endDate={filters.endDate}
  selectedRow={selectedMapRow}     // 👈 ADD THIS
  onClearSelection={() => setSelectedMapRow(null)}
/>
      </Box>
   
    <Box sx={{ backgroundColor: "white", borderRadius: 2, border: "2px solid #091E4224", py: 2, m: 1 }}>
      {/* Header */}
      <Box
        sx={{
          pb: 2,
          px: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
    
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pb: 1 }}>
      <CustomText sx={{ ...textStyles.h1, fontWeight: 600, fontSize: "18px", color: "#003149" }}>
        {t("attendanceHistory.heading")}
      </CustomText>
      {tableLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
    </Box>

        {/* Search */}
        <Box
          sx={{
            px: 1,
            py: 0.2,
            borderRadius: 1,
            height: 34,
            border: "2px solid #091E4224",
            display: "flex",
            alignItems: "center",
            minWidth: 250,
            maxWidth: 300,
          }}
        >
          <InputBase
            placeholder={t("attendanceHistory.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              ml: 0.5,
              fontSize: 15,
              fontFamily: "Poppins, sans-serif",
              width: "100%",
            }}
          />
          <SearchIcon sx={{ fontSize: 22, color: "#888", mr: 0.5 }} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
         
          <ExportDropdown
  onDownloadPDF={async () => {
    const allData = await fetchAllHistory(decryptedToken, filters); // ✅ fetch all pages
    const blob = await pdf(<ExportAttendanceHistoryPDF data={allData} logo={logoBase64} />).toBlob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Attendance_History.pdf";
    link.click();
  }}
  onDownloadExcel={async () => {
    const allData = await fetchAllHistory(decryptedToken, filters); // ✅ fetch all pages
    ExportAttendanceHistoryExcel(allData);
  }}
/>
              {isFilterApplied ? (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 34,
        borderRadius: 1,
        bgcolor: primaryColor,
      }}
    >
      <FilterAltIcon sx={{ color: primaryTextColor, fontSize: 20 }} />

      {/* Small Clear Button */}
      <IconButton
        size="small"
        onClick={() => {
          setFilters({ status: "", startDate: "", endDate: "" ,  workerId: ""});
          setCurrentPage(1);
          setIsFilterApplied(false);
        }}
        sx={{
          position: "absolute",
          top: -6,
          right: -6,
          bgcolor: "white",
          width: 18,
          height: 18,
          border: "1px solid #C4C4C4",
          "&:hover": { 
            bgcolor: `${secondaryColor}10`,
            borderColor: secondaryColor,
          },
          transition: "all 0.3s ease",
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 14, color: secondaryTextColor }} />
      </IconButton>
    </Box>
  ) : (
    <IconButton
      onClick={() => setFilterOpen(true)}
      sx={{
        border: "1px solid #C4C4C4",
        borderRadius: 1,
        width: 34,
        height: 34,
        "&:hover": { 
          backgroundColor: `${primaryColor}10`,
          borderColor: primaryColor,
        },
        transition: "all 0.3s ease",
      }}
    >
      <FilterAltIcon />
    </IconButton>
  )}
        </Box>
      </Box>

      {/* Tabs content */}
      {activeTab === "map" && <AttendanceMap />}
      {/* {activeTab === "calendar" && <AttendanceCalendar />} */}
      {activeTab === "list" && (
        <>
        {initialLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
              <CircularProgress />
            </Box>
          ) : filteredAll.length === 0 && !(searchActive && tableLoading) ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 5,
              }}
            >
              <img src={empty} alt="No Data" style={{ width: 200, marginBottom: 16 }} />
              <Typography sx={{ fontSize: "15px" }}>{t("empty.no_user")}</Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table>
                  <TableHead
                    sx={{
                      borderTop: "2px solid #091E4224",
                      borderBottom: "2px solid #091E4224",
                    }}
                  >
                    <TableRow>
                      {/* <TableCell sx={HeaderStyles}>{t("attendanceHistory.tableHeaders.id")}</TableCell> */}
                      <TableCell sx={HeaderStyles}>{t("attendanceHistory.tableHeaders.fullName")}</TableCell>
                      <TableCell sx={HeaderStyles}>{t("attendanceHistory.tableHeaders.email")}</TableCell>

                      <TableCell sx={HeaderStyles}>{t("attendanceHistory.tableHeaders.date")}</TableCell>
                      <TableCell sx={HeaderStyles}>{t("clockIn")}</TableCell>
                      <TableCell sx={HeaderStyles}>{t("breakStart")}</TableCell>
<TableCell sx={HeaderStyles}>{t("breakEnd")}</TableCell>


                      <TableCell sx={HeaderStyles}>{t("clockOut")}</TableCell>
                      <TableCell sx={HeaderStyles}>{t("attendanceHistory.tableHeaders.location")}</TableCell>
                      <TableCell sx={HeaderStyles}>{t("attendanceHistory.tableHeaders.details")}</TableCell>
                      <TableCell sx={HeaderStyles}>{t("attendanceHistory.tableHeaders.status")}</TableCell>
                      <TableCell sx={HeaderStyles}>{t("attendanceHistory.tableHeaders.action")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowsForTable.map((row, index) => (
                      <TableRow key={index} 
                        hover
  sx={{ cursor: "pointer" }}
  onClick={() => {
    setSelectedMapRow(row.raw);   // 👈 pass full record
  }}>
                        {/* <TableCell sx={DataStyles}>{row.id}</TableCell> */}
                        <TableCell sx={DataStyles}>{row.fullName}</TableCell>
                        <TableCell sx={DataStyles}>{row.email}</TableCell>
                        <TableCell sx={DataStyles}>{row.date}</TableCell>
                       <TableCell sx={DataStyles}>
  {row.checkIns?.length > 0
    ? getCompactTimes(row.checkIns).map((time, i) => (
        renderCompactTimeCell(time, i)
      ))
    : formatTimeTo12Hour(row.checkIn)}
</TableCell>
<TableCell sx={DataStyles}>
  {row.breaks?.length > 0
    ? getCompactTimes(row.breaks.map((b) => b?.start)).map((time, i) => (
        renderCompactTimeCell(time, i)
      ))
    : "--"}
</TableCell>

<TableCell sx={DataStyles}>
  {row.breaks?.length > 0
    ? getCompactTimes(row.breaks.map((b) => b?.end)).map((time, i) => (
        renderCompactTimeCell(time, i)
      ))
    : "--"}
</TableCell>

<TableCell sx={DataStyles}>
  {row.checkOuts?.length > 0
    ? getCompactTimes(row.checkOuts).map((time, i) => (
        renderCompactTimeCell(time, i)
      ))
    : formatTimeTo12Hour(row.checkOut)}
</TableCell>
                        <TableCell sx={DataStyles}><TruncatedCell text={row.location} /></TableCell>
                        <TableCell sx={DataStyles}><TruncatedCell text={row.details} /></TableCell>
                        <TableCell sx={DataStyles}>
                          <Chip
                            label={formatStatus(row.status)}
                            sx={{
                              backgroundColor: getStatusBadgeBackgroundColor(
                                getAttendanceHistoryColorKey(row.status)
                              ),
                              color: getStatusBadgeForegroundColor(
                                getAttendanceHistoryColorKey(row.status)
                              ),
                              fontSize: "13px",
                              fontWeight: 500,
                              fontFamily: "Poppins, sans-serif",
                              height: "30px",
                              borderRadius: "4px",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={DataStyles}>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={paginationCount}
                  page={effectiveSearchPage}
                  onChange={(_, value) => setCurrentPage(value)}
                  shape="rounded"
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                    },
                    "& .Mui-selected": {
                      backgroundColor: `${primaryColor}10`,
                      color: primaryColor,
                    },
                  }}
                />
              </Box>
            </>
          )}
        </>
      )}

      {/* Details Drawer */}
      <AttendanceDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedRow={selectedRow}
      />

      {/* Row Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            setSelectedRow(menuRow);
            setDrawerOpen(true);
            handleMenuClose();
          }}
          sx={{ fontSize: "13px" }}
        >
          {t("unvalidatedPunches.tableActions.view")}
        </MenuItem>
      </Menu>

      {/* <AttendanceFilterDrawer
  open={filterOpen}
  onClose={() => setFilterOpen(false)}
  filters={filters}  // Pass current filters
  setFilters={setFilters}  // Update filters
  onApplyFilters={handleApplyFilters}
/> */}
<AttendanceFilterDrawer
  open={filterOpen}
  onClose={() => setFilterOpen(false)}
  filters={filters}
  setFilters={setFilters}
  setIsFilterApplied={setIsFilterApplied}
/>
 
    </Box>
    </>
  );
};

export default AttendanceHistory;


















